"use client";
import TestimonialsClient from "./TestimonialsClient";
import { TestimonialsData } from "@/types/data";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

export default function Testimonials({
  data,
}: {
  data: TestimonialsData["content"];
}) {
  const { tagline, title, description, testimonials } = data;
  const duplicatedTestimonials = {
    ...data,
    testimonials: [
      ...testimonials,
      ...testimonials,
      ...testimonials,
      ...testimonials,
    ],
  };
  

  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  
  useEffect(() => {
    const ctx = gsap.context(() => {
      
      const elementsToAnimate = [
        headerRef.current,
        headingRef.current,
        descriptionRef.current,
      ].filter(Boolean);

      if (elementsToAnimate.length > 0) {
        gsap.set(elementsToAnimate, {
          opacity: 0,
          y: 40,
          scale: 0.95,
          filter: "blur(8px)",
        });
      }

      
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "top 45%",
          toggleActions: "play none none reverse",
        },
      });

      
      if (headerRef.current) {
        timeline.to(
          headerRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            duration: 0.5,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.5"
        );
      }

      
      if (headingRef.current) {
        timeline.to(
          headingRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.5"
        );
      }

      
      if (descriptionRef.current) {
        timeline.to(
          descriptionRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.5"
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-white w-full py-16 sm:py-[70px] overflow-hidden"
    >
      {}
      <div className=" container mx-auto text-center mb-12 px-4 sm:px-6 lg:px-8">
        {tagline && (
          <div
            ref={headerRef}
            className="flex items-center justify-center  mb-4"
          >
            <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
            <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
              {tagline}
            </p>
          </div>
        )}
        {title && (
          <h2
            ref={headingRef}
            className="text-4xl md:text-5xl font-bold text-theme-secondary"
          >
            {title}
          </h2>
        )}
        {description && (
          <p
            ref={descriptionRef}
            className="mt-[14px] text-lg leading-[31.5px] text-theme-secondary-light max-w-[530px] mx-auto "
          >
            {description}
          </p>
        )}
      </div>

      {}

      <TestimonialsClient duplicatedTestimonials={duplicatedTestimonials} />
    </section>
  );
}
